import {Component, computed, effect, inject, input, OnInit, output, signal, untracked} from '@angular/core';
import {MaterialModule} from '../../../../../core/modules/material.module';
import {SharedModule} from '../../../../../core/modules/shared.module';
import {form, FormField, required} from '@angular/forms/signals';
import {isEqual} from 'lodash';
import {ParishOrganizationService} from '../../../../../core/services/parishOrganization.service';
import {OrganizationsService} from '../../../../../core/services/organizations.service';
import {FuseConfigService} from '../../../../../core/services/config.service';
import {NgxSpinnerService} from 'ngx-spinner';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {FuseDeleteDialogComponent} from '../../../../../core/components/delete-dialog/delete-dialog.component';
import {PagesContainerComponent} from '../../../pages-container/pages-container.component';

export interface ParishOrganization {
  id?: number;
  name: string;
  countryId: number | null;
  cityId: number | null;
  responsible_first_name: string;
  responsible_last_name: string;
  responsible_email: string;
  president_first_name: string;
  president_last_name: string;
  president_email: string;
  status: boolean | number;
}

const initialData: ParishOrganization = {
  name: '',
  countryId: null,
  cityId: null,
  responsible_first_name: '',
  responsible_last_name: '',
  responsible_email: '',
  president_first_name: '',
  president_last_name: '',
  president_email: '',
  status: 0
};

@Component({
  selector: 'parish-organization-detail',
  templateUrl: './parishOrganization-detail.component.html',
  styleUrls: ['./parishOrganization-detail.component.scss'],
  imports: [MaterialModule, SharedModule, FormField, PagesContainerComponent],
  providers: [OrganizationsService, ToastrService, TranslateService]
})
export class ParishOrganizationDetailComponent implements OnInit {
  private parishOrgService = inject(ParishOrganizationService);
  private orgService = inject(OrganizationsService);
  private fuseConfig = inject(FuseConfigService);
  private spinner = inject(NgxSpinnerService);
  private toastr = inject(ToastrService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);

  formResult = output<any>();
  isValid = output<boolean>();
  isChangedOutput = output<boolean>();
  parishOrgData = input<any>();

  id = signal<string | number | null>(null);

  parishOrgModel = signal<ParishOrganization>(initialData);
  countries = signal<any[]>([]);
  cities = signal<any[]>([]);

  initialLoadedData = signal<ParishOrganization | null>(null);

  role = this.fuseConfig.role_Sig;

  parishOrgForm = form(this.parishOrgModel, (org) => {
    required(org.name, {message: 'event.is_required_field'});
    required(org.countryId, {message: 'event.is_required_field'});
    required(org.cityId, {message: 'event.is_required_field'});
  });

  isChanged = computed(() => {
    const referenceData = this.initialLoadedData() || initialData;
    return !isEqual(this.parishOrgModel(), referenceData);
  });

  selectedCountryId = computed(() => this.parishOrgModel().countryId);

  constructor() {
    this.route.queryParams.subscribe(params => {
      if (params['id']) {
        this.id.set(params['id']);
        this.loadParishOrganization(params['id']);
      }
    });

    effect(() => {
      const data = this.parishOrgData();
      if (data) {
        untracked(() => {
          this.parishOrgModel.update(e => ({...e, ...data}));
          this.initialLoadedData.set(JSON.parse(JSON.stringify(this.parishOrgModel())));
        });
      }
    });

    effect(() => {
      const countryId = this.selectedCountryId();
      if (countryId) {
        untracked(() => {
          this.getCantons(countryId);
        });
      } else {
        this.cities.set([]);
      }
    });

    effect(() => {
      const result = this.parishOrgModel();
      untracked(() => {
        this.formResult.emit(result);
        this.isValid.emit(this.parishOrgForm().valid());
        this.isChangedOutput.emit(this.isChanged());
      });
    });
  }

  ngOnInit() {
    this.getCountries();
  }

  getCountries() {
    this.orgService.getCountry().subscribe((res: any) => {
      if (res.length === 1) {
        this.getCantons(res[0].id);
        this.parishOrgModel.update(e => ({...e, countryId: res[0].id}));
      } else {
        this.countries.set(res);
      }
    });
  }

  getCantons(id: any) {
    this.orgService.getCity(id).subscribe((res: any) => {
      const currentCityId = this.parishOrgModel().cityId;
      if (res.length === 1) {
        this.parishOrgModel.update(e => ({...e, cityId: res[0].id}));
      } else {
        this.cities.set(res);
      }
      if (currentCityId && res.find((c: any) => c.id === currentCityId)) {
        this.parishOrgModel.update(e => ({...e, cityId: currentCityId}));
      }
    });
  }

  loadParishOrganization(id: string | number) {
    this.spinner.show();
    this.parishOrgService.findById(id).subscribe({
      next: (res: any) => {
        const data = res?.data || res;
        if (data) {
          this.parishOrgModel.set({
            ...data,
            status: data.status ? 1 : 0
          });
          this.initialLoadedData.set(JSON.parse(JSON.stringify(this.parishOrgModel())));
        }
        this.spinner.hide();
      },
      error: () => {
        this.spinner.hide();
      }
    });
  }

  save() {
    if (!this.parishOrgForm().valid()) {
      return;
    }
    this.spinner.show();
    const data = this.parishOrgModel();
    this.parishOrgService.addParishOrganization(data).subscribe({
      next: () => {
        this.spinner.hide();
        this.toastr.success(this.translate.instant('successfully'), '');
        this.router.navigate(['/pages/settings/parishOrganization']);
      },
      error: () => {
        this.spinner.hide();
      }
    });
  }

  delete() {
    const dialogRef = this.dialog.open(FuseDeleteDialogComponent, {
      panelClass: 'unsave-form-dialog',
      width: '300px',
      data: {title: 'Delete', message: 'deleteConfirm'}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const id = this.id();
        if (id) {
          this.spinner.show();
          this.parishOrgService.deleteParishOrganization(id).subscribe({
            next: () => {
              this.spinner.hide();
              this.toastr.success(this.translate.instant('successfully'), '');
              this.router.navigate(['/pages/settings/parishOrganization']);
            },
            error: () => {
              this.spinner.hide();
            }
          });
        }
      }
    });
  }

  back() {
    this.router.navigate(['/pages/settings/parishOrganization']);
  }
}
